import { type ImputationMethod } from '@ebalance/proto/bffuser/v1/imputation_policy_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  type BoxProps,
  RadioGroupItem as EcRadioGroupItem,
  RadioGroupItemText as EcRadioGroupItemText,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import {
  DEMAND_METHODS,
  METHOD_I18N_KEYS,
} from '~/pages/BalanceImputationPolicy/constants/defaults'

// chakra v3.29 + React 18 types の既知不具合で `RadioGroupItemProps` /
// `RadioGroupItemTextProps` (= `extends HTMLChakraProps<...>, UnstyledProp`) が
// `{ unstyled }` のみに collapse し、children を含む全 props を JSX で受け取れなくなる。
// ecloud 由来の component をそのまま使い、取りこぼした props を BoxProps で型付けし直す
// (`pages/SpotBidding/components/IndividualBidModal.tsx` の FieldLabel と同じ回避)。
const RadioGroupItem = EcRadioGroupItem as React.FC<
  BoxProps & { value: string }
>
const RadioGroupItemText = EcRadioGroupItemText as React.FC<BoxProps>

type Props = {
  value: ImputationMethod
  onChange: (value: ImputationMethod) => void
  /** 保存失敗時に増える再同期トークン (useImputationPolicy の godoc 参照)。 */
  resetToken: number
  /** 保存中は操作させない (= 保存の直列化。useImputationPolicy の godoc 参照)。 */
  disabled: boolean
}

/**
 * 需要側の補完方式ラジオ (spec AC-001 / AC-002 / AC-003)。
 * design `balance/imputation-policy.html` の `.ipe-radio-card` 相当で、
 * 方式ごとに枠線カード化し説明文をカード内に併記する。選択と同時に即時保存する
 * (保存ボタンなし)。
 *
 * ## [DS 差異] `RadioButton` ではなく radio-group の slot を直接使う
 *
 * design (`design/css/components.css` の `.ecl-radio-card`) は
 * **方式名 = 14px/700/highEmphasis + 説明文 = 12px/midEmphasis** の 2 段組みが正。
 * DS `RadioButton` の `radios[].label` は **`string` 固定**で ReactNode を取れず、
 * 旧実装は 1 つの label に `\n` で 2 行を詰めていたため両行が同じ font-size になり
 * 主従が付かなかった (issue #4884)。
 *
 * そこで `RadioButton` が内部で使っているのと**同じ radio-group の slot**
 * (`RadioGroupRoot` / `Item` / `ItemControl` / `ItemText`) を直接組み、
 * `ItemText` の中で方式名と説明文を別要素に分ける。
 *
 * **`RadioCard` (chakra の card 型 radio) は採らない。** `itemDescription` slot を
 * 持ち一見適任だが、recipe が `radioCard` で **`radioGroup` とは別物**のため、
 * `constants/system.ts` が radio に当てている DS 補正 (未選択枠 2px / ラベル 14px)
 * が効かず、丸の見た目がアプリ内の他の radio と食い違う。
 *
 * DS `RadioButton` の `label` が ReactNode を取れるようになれば、この slot 直接
 * 利用は畳んで `RadioButton` へ戻せる (呼び出し側の props は変えていない)。
 */
export const DemandMethodRadio = ({
  value,
  onChange,
  resetToken,
  disabled,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const handleChange = (v: string | null): void => {
    const next = DEMAND_METHODS.find((m) => String(m) === v)
    if (next !== undefined) {
      onChange(next)
    }
  }

  return (
    <Box maxWidth="720px">
      <RadioGroupRoot
        // 保存失敗時は value prop が「クリック前の値」= 変化なしに戻るため、
        // DS 内部の選択状態 (= ユーザーのクリック) を prop 変化で戻せない。
        // resetToken を key に混ぜて再マウントし、rollback を画面に反映する。
        key={`${String(value)}-${String(resetToken)}`}
        aria-label={t('page:balanceImputationPolicy.demand.label')}
        disabled={disabled}
        name="demandMethod"
        onValueChange={(details: { value: string | null }) => {
          handleChange(details.value)
        }}
        value={String(value)}
      >
        <VStack align="stretch" gap={2} width="100%">
          {DEMAND_METHODS.map((method) => (
            <RadioGroupItem
              key={String(method)}
              // design `.ecl-radio-card` (components.css): 枠線カード + **縦センター**。
              // 「コントロールは縮めない。 位置は親の align-items:center が決める
              // (縦センター)」 と design 側に明記がある。 旧実装の `flex-start` は
              // 2026-07-28 に共通 `.ecl-radio-card` へ移行する前の
              // `.ipe-radio-card` (上揃え) に基づくもので、 移行後は古い記述だった
              // (2026-09-01 デザイナー指摘「radio ボタンはカード内で縦センターに配置」)。
              alignItems="center"
              gap={3}
              width="100%"
              // UI 辞書 components/radio-card.md の state (8080 `.ecl-radio-card`):
              // padding 16px (12px だと枠と文字が近く「カード」に見えない) / hover は地
              // Surface.secondary (選択中は変えない = 「選べる」と「選ばれている」を見分ける) /
              // checked は枠 Surface.accentPrimary + 地 Surface.accentPrimaryLightLow /
              // disabled は cursor not-allowed + 文字 Text.disabled
              padding={4}
              borderWidth="1px"
              borderColor="Border.midEmphasis"
              borderRadius="lg"
              _hover={{
                backgroundColor: 'Surface.secondary',
                _checked: { backgroundColor: 'Surface.accentPrimaryLightLow' },
              }}
              _checked={{
                borderColor: 'Surface.accentPrimary',
                backgroundColor: 'Surface.accentPrimaryLightLow',
              }}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              // design `.ipe-radio-card:has(input:checked)`: 選択中はアクセント
              // (design の --surface-accent-primary-light-low に対応)。
              backgroundColor="Surface.primary"
              value={String(method)}
            >
              <RadioGroupItemHiddenInput />
              <RadioGroupItemControl />
              <RadioGroupItemText textAlign="left">
                {/* design `.ecl-radio-card__title` = md (14px) / 700 / highEmphasis */}
                <Text
                  textStyle="radioCardTitle"
                  color={disabled ? 'Text.disabled' : 'Text.highEmphasis'}
                  lineHeight={1.5}
                >
                  {t(METHOD_I18N_KEYS[method].name)}
                </Text>
                {/* design `.ecl-radio-card__desc` = sm (12px) / midEmphasis */}
                <Text
                  textStyle="radioCardDesc"
                  color={disabled ? 'Text.disabled' : 'Text.midEmphasis'}
                  lineHeight={1.5}
                >
                  {t(METHOD_I18N_KEYS[method].description)}
                </Text>
              </RadioGroupItemText>
            </RadioGroupItem>
          ))}
        </VStack>
      </RadioGroupRoot>
    </Box>
  )
}
